/**
 * YalaFloodOpsBrief — the provincial flood-command operating picture.
 *
 * Distinct from FloodBrief (live sensor telemetry), this shows the Yala
 * Provincial Office's own latest flood *situation report* from data.go.th:
 *  • affected villages (by district)   • evacuees sheltering (by location)
 *  • disaster-response equipment + who to call
 *  • standing early-warning asset inventory
 *
 * Headline stats are always visible; the operational detail (top shelters,
 * response equipment quick-dial, warning assets) is a collapsed <details>,
 * same native pattern as FloodBrief's river-forecast block.
 */

import type { YalaFloodOps, FallbackTier } from "@yala/shared";
import { PanelHeader } from "./PanelHeader";
import { useT } from "../hooks/useLang";

interface Props {
  data: YalaFloodOps | null;
  loading?: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

export function YalaFloodOpsBrief({ data, loading, ageMinutes, fallbackTier }: Props) {
  const t = useT();

  const topDistrict = data?.affectedByDistrict[0] ?? null;

  return (
    <div className="col flood-brief">
      <PanelHeader
        title={t("FLOOD OPS // YALA PROVINCE", "ศูนย์บัญชาการอุทกภัย // จังหวัดยะลา")}
        source="data.go.th / yala-province"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
      />

      {!data ? (
        <div className="mono eyebrow" style={{ color: "var(--text-3)", marginTop: 8 }}>
          {loading ? t("loading provincial report…", "กำลังโหลดรายงานจังหวัด…") : t("no provincial report", "ไม่มีรายงานจังหวัด")}
        </div>
      ) : (
        <>
          {/* Headline: affected villages · evacuees · shelters */}
          <div className="flood-row" style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div className="eyebrow">{t("AFFECTED VILLAGES", "หมู่บ้านประสบภัย")}</div>
              <div className="mono" style={{ fontSize: "1.0rem", color: "var(--warn)" }}>
                {data.affectedVillages.toLocaleString()}
              </div>
              <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                {t(`across ${data.affectedByDistrict.length} districts`, `${data.affectedByDistrict.length} อำเภอ`)}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div className="eyebrow">{t("EVACUEES", "ผู้อพยพ")}</div>
              <div className="mono" style={{ fontSize: "1.0rem", color: "var(--bad)" }}>
                {data.evacueesTotal.toLocaleString()}
              </div>
              <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                {t(`in ${data.shelterCount} shelters`, `ใน ${data.shelterCount} ศูนย์พักพิง`)}
              </div>
            </div>
          </div>

          {/* Worst-hit district */}
          {topDistrict && (
            <div style={{ marginTop: 10 }}>
              <div className="eyebrow">{t("WORST-HIT DISTRICT", "อำเภอที่ประสบภัยมากสุด")}</div>
              <div className="mono" style={{ fontSize: "0.9rem" }}>
                {topDistrict.district} ·{" "}
                <span style={{ color: "var(--warn)" }}>
                  {t(`${topDistrict.villages} villages`, `${topDistrict.villages} หมู่บ้าน`)}
                </span>
              </div>
            </div>
          )}

          {/* Operational detail — collapsed by default (native <details>, no state hook). */}
          <details className="detail-disclosure">
            <summary className="mono">{t("Operations detail", "รายละเอียดปฏิบัติการ")}</summary>
            <div className="detail-disclosure-body">
              {/* Top shelters by headcount */}
              {data.topShelters.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div className="eyebrow">{t("LARGEST SHELTERS", "ศูนย์พักพิงที่ใหญ่สุด")}</div>
                  {data.topShelters.map((s, i) => (
                    /* Wrap to a second line rather than ellipsise the name. The
                       name is where the evacuees are — cutting it at 219px in
                       the rail lost the sub-district, which is the part that
                       tells an operator where to send a truck. The headcount
                       stays on the right. */
                    <div
                      key={`${s.name}-${i}`}
                      className="mono"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "2px 8px",
                        fontSize: "0.8rem",
                        marginTop: 2,
                        alignItems: "baseline",
                      }}
                    >
                      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {s.name}
                        {s.subdistrict ? <span style={{ color: "var(--text-3)" }}> · {s.subdistrict}</span> : null}
                      </span>
                      <span style={{ color: "var(--bad)", flexShrink: 0 }}>{s.persons.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Response equipment quick-dial */}
              {data.equipment.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div className="eyebrow">{t("RESPONSE EQUIPMENT — DISPATCH", "เครื่องจักรกลสาธารณภัย — สายด่วน")}</div>
                  {data.equipment.map((e, i) => (
                    /* Wrap to a second line instead of truncating. The item string
                       ends with the QUANTITY ("จำนวน 8 คัน") and a single-line
                       ellipsis cut exactly that off — the one number an operator
                       dialing for a truck needs. The row also used to push the
                       rail into 47px of horizontal overflow at 1024, because the
                       TEL link could not shrink. */
                    <div
                      key={`${e.item}-${i}`}
                      className="mono"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "2px 10px",
                        fontSize: "0.8rem",
                        marginTop: 2,
                        alignItems: "baseline",
                      }}
                    >
                      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{e.item}</span>
                      {e.phone ? (
                        <a href={telHref(e.phone)} style={{ color: "var(--data)", flexShrink: 0, textDecoration: "none" }}>
                          {t("TEL", "โทร")} {e.phone}
                        </a>
                      ) : (
                        <span style={{ color: "var(--text-3)", flexShrink: 0 }}>—</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Early-warning asset inventory */}
              {data.warningAssets.length > 0 && (
                <div>
                  <div className="eyebrow">{t("EARLY-WARNING ASSETS", "ระบบเตือนภัยล่วงหน้า")}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 2 }}>
                    {data.warningAssets.map((a, i) => (
                      <span key={`${a.label}-${i}`} className="mono" style={{ fontSize: "0.8rem" }}>
                        {a.label.trim()}
                        {a.count != null ? (
                          <span style={{ color: "var(--good)" }}> {a.count.toLocaleString()}{a.unit ? ` ${a.unit}` : ""}</span>
                        ) : null}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>

          <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 8 }}>
            {t(
              "Provincial situation report · สำนักงานจังหวัดยะลา (data.go.th)",
              "รายงานสถานการณ์จังหวัด · สำนักงานจังหวัดยะลา (data.go.th)",
            )}
          </div>
        </>
      )}
    </div>
  );
}
